// Scheme guard for user-supplied URLs that get rendered as clickable links.
//
// An entry's `url` is free text: typed by the user, or imported wholesale from
// a 1PIF/CSV file that may have come from anywhere. Putting it straight into an
// <a href> makes `javascript:…` (and `data:`/`vbscript:`) executable in the
// vault's own origin — with the derived key and the decrypted vault live in
// memory. The page CSP still carries 'unsafe-inline' in script-src (the Puter
// SDK needs it), so CSP does not block those schemes; this module is the
// control that does.
//
// Returns an href string only for schemes that are inert to click, and null for
// everything else so the caller can render the raw text instead of a link.

const SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'ftp:']);

const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * @param {unknown} input raw user/imported value
 * @returns {string|null} an href safe to place in <a href>, or null if the
 *   value must not be linkified.
 */
export function safeHttpUrl(input) {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;

  // Reject control characters outright. Browsers strip \t, \n and \r while
  // parsing a scheme, so "java\tscript:alert(1)" navigates as javascript: even
  // though it fails a naive prefix check.
  if (/[ -]/.test(trimmed)) return null;

  // Bare "example.com" — no scheme at all — is the common way people record a
  // site. Treat it as https, matching how favicon.js derives a host.
  const candidate = HAS_SCHEME.test(trimmed) ? trimmed : `https://${trimmed}`;

  let u;
  try {
    u = new URL(candidate);
  } catch {
    return null;
  }
  if (!SAFE_PROTOCOLS.has(u.protocol.toLowerCase())) return null;
  return u.href;
}
